/**
 * Field Agent API - Management of field agent survey links
 */

import { api } from './client'

/**
 * Create a unique survey link for a field agent
 *
 * @param {string} surveyId - Survey UUID
 * @param {string} userId - Field agent user UUID
 * @param {Object} options
 * @param {number} options.assigned_location_lat - Latitude of assigned location
 * @param {number} options.assigned_location_lng - Longitude of assigned location
 * @param {string} options.assigned_location_name - Name of assigned location
 * @param {number} options.allowed_radius_meters - Custom radius override
 * @returns {Promise<Object>} Created field agent link
 */
export async function createFieldAgentLink(surveyId, userId, options = {}) {
  return api.post(`/surveys/${surveyId}/field-agents/${userId}/link`, {
    assigned_location_lat: options.assigned_location_lat,
    assigned_location_lng: options.assigned_location_lng,
    assigned_location_name: options.assigned_location_name,
    allowed_radius_meters: options.allowed_radius_meters,
  })
}

/**
 * List all field agents assigned to a survey with their statistics
 *
 * @param {string} surveyId - Survey UUID
 * @returns {Promise<Array>} List of field agents with stats
 */
export async function listFieldAgents(surveyId) {
  return api.get(`/surveys/${surveyId}/field-agents`)
}

/**
 * Get a specific field agent's link for a survey
 *
 * @param {string} surveyId - Survey UUID
 * @param {string} userId - Field agent user UUID
 * @returns {Promise<Object>} Field agent link
 */
export async function getFieldAgentLink(surveyId, userId) {
  return api.get(`/surveys/${surveyId}/field-agents/${userId}/link`)
}

/**
 * Update a field agent's link settings
 *
 * @param {string} surveyId - Survey UUID
 * @param {string} userId - Field agent user UUID
 * @param {Object} updates
 * @returns {Promise<Object>} Updated link
 */
export async function updateFieldAgentLink(surveyId, userId, updates) {
  return api.patch(`/surveys/${surveyId}/field-agents/${userId}/link`, updates)
}

/**
 * Deactivate a field agent's link
 *
 * @param {string} surveyId - Survey UUID
 * @param {string} userId - Field agent user UUID
 * @returns {Promise<Object>} Success response
 */
export async function deactivateFieldAgentLink(surveyId, userId) {
  return api.delete(`/surveys/${surveyId}/field-agents/${userId}/link`)
}

/**
 * Get a survey via field agent token (public endpoint)
 *
 * @param {string} token - Unique field agent token
 * @returns {Promise<Object>} Survey data
 */
export async function getSurveyByToken(token) {
  return api.get(`/survey/${token}`)
}

/**
 * Submit a response via field agent token
 *
 * @param {string} token - Unique field agent token
 * @param {Object} responseData - Response data
 * @returns {Promise<Object>} Response data
 */
export async function submitResponseViaToken(token, responseData) {
  return api.post(`/survey/${token}/responses`, responseData)
}

/**
 * Get all survey assignments for the current user (field agent)
 *
 * Returns the list of surveys assigned to the current user with their
 * unique tokens for taking surveys.
 *
 * @returns {Promise<Array>} List of field agent links with survey info
 */
export async function getMyAssignments() {
  return api.get('/my-assignments')
}
